import { inject, Injectable } from '@angular/core';
import { ApiAnnotation, ApiSpine } from '@mhe/reader/models';
import { MaterialAnnotationType } from './material-annotation-list.model';
import { Store } from '@ngrx/store';
import * as annotationsActions from '@mhe/reader/global-store/annotations/annotations.actions';
import { Observable } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import * as annotationsQuery from '@mhe/reader/global-store/annotations/annotations.selectors';
import { ReaderStore } from '../reader/state/reader.store';
import { Annotations } from '@mhe/reader/global-store/annotations/annotations.actions';

export interface annotationTypeVal {
  annotations: ApiAnnotation[]
  label: string
  spineId: string
};

export interface AnnotationsByType {
  annotations: ApiAnnotation[]
  annotationsByChapter: annotationTypeVal[]
}

@Injectable({
  providedIn: 'root',
})
export class MaterialAnnotationsListService {
  readonly store = inject(Store);
  readonly readerStore = inject(ReaderStore);

  /**
   * Recurse into the TOC and get all spineItems with subItems as a single array.
   * The order will represent the same order as if the user were using the arrow navigation.
   */
  readonly flattenedToc$ = this.readerStore.flatToc$.pipe(
    filter((x) => x !== undefined),
    map((flatToc) => {
      const accumulateItems = (items: any[]): any[] => {
        return items.reduce((acc, item) => {
          if (!acc.some(existing => existing.id === item.spineItem.id)) {
            acc.push({
              id: item.spineItem.id,
              nodeName: item.nodeName,
              label: item.label,
            });
          }
          if (item.subItems && Array.isArray(item.subItems)) {
            const subItems = accumulateItems(item.subItems);
            subItems.forEach(subItem => {
              if (!acc.some(existing => existing.id === subItem.id)) {
                acc.push(subItem);
              }
            });
          }
          return acc;
        }, []);
      };

      return accumulateItems(Object.values(flatToc));
    }),
  );

  /**
   * This is an ordered array of just spineIds and represents the same TOC order.
   * Useful for the sort() function.
   */
  readonly flattenedTocForSorting$ = this.flattenedToc$.pipe(
    map((flattenedToc) => flattenedToc.map(spineItem => spineItem.id)),
  );

  readonly annotationsListByType = (type: MaterialAnnotationType): Observable<AnnotationsByType> =>
    this.store.select(annotationsQuery.getAnnotations).pipe(
      withLatestFrom(
        this.store.select(annotationsQuery.getSpines),
        this.flattenedToc$,
        this.flattenedTocForSorting$,
      ),
      map(([annotations, spines, flattenedToc, flattenedTocForSorting]) => {
        // get spines from the store with indicators for whether they have annotations
        const listByType = spines.map((spine: ApiSpine): annotationTypeVal | undefined => {
          if (type === MaterialAnnotationType.HIGHLIGHTS) {
            if (spine.hasHighlight) {
              return {
                label: spine.groupLabel as string,
                annotations: [],
                spineId: spine.spineID,
              } satisfies annotationTypeVal;
            }
          }
          if (type === MaterialAnnotationType.NOTES) {
            if (spine.hasNote) {
              return {
                label: spine.groupLabel as string,
                annotations: [],
                spineId: spine.spineID,
              } satisfies annotationTypeVal;
            }
          }
          if (type === MaterialAnnotationType.PLACEMARKS) {
            if (spine.hasPlacemark) {
              return {
                label: spine.groupLabel as string,
                annotations: [],
                spineId: spine.spineID,
              } satisfies annotationTypeVal;
            }
          }
          return undefined;
        });

        // filter out spines without annotations of the type requested
        const spinesListByType = listByType.filter((spine) => spine !== undefined);

        // if spine label is undefined, try to find it in the flat TOC
        spinesListByType.forEach((spine) => {
          if (spine.label === undefined) {
            const tocEntry = flattenedToc.find(item => item.id === spine.spineId);
            if (tocEntry) {
              spine.label = tocEntry.label;
            }
          }
        });

        const groupedAnnotations = Object.groupBy(annotations, spine => spine.spineID);
        const spinesWithAnnotations = spinesListByType.map((spine: annotationTypeVal) => {
          if (groupedAnnotations[spine.spineId]) {
            return {
              ...spine,
              annotations: [
                ...spine.annotations,
                ...(groupedAnnotations[spine.spineId] as ApiAnnotation[]).filter(annotation => {
                  if (type === MaterialAnnotationType.HIGHLIGHTS) {
                    return annotation.highlight;
                  }
                  if (type === MaterialAnnotationType.NOTES) {
                    return annotation.note;
                  }
                  if (type === MaterialAnnotationType.PLACEMARKS) {
                    return annotation.placemarkText;
                  }
                  return false;
                }),
              ],
            } satisfies annotationTypeVal;
          }
          return spine;
        });

        // apply toc ordering for consistency
        spinesWithAnnotations.sort(
          (a, b) => flattenedTocForSorting.indexOf(a.spineId) - flattenedTocForSorting.indexOf(b.spineId),
        );

        return {
          annotations,
          annotationsByChapter: spinesWithAnnotations,
        };
      }),
    );

  spineIdExpanded = (spineId: string): void => {
    this.store.dispatch(annotationsActions.fetchAnnotationBySpineId({ spineId }));
  };

  annotationListItemClicked = (annotation: ApiAnnotation): void => {
    this.store.dispatch(annotationsActions.clickedAnnotation({ annotation }));
  };

  deleteAnnotationListItem = (annotation: ApiAnnotation): void => {
    this.store.dispatch(annotationsActions.removeAnnotationFromList({ annotation }));
  };

  selectAllAnnotationsInList = (selected: boolean): void => {
    this.store.dispatch(annotationsActions.selectAllAnnotationsInList({ selected }));
  };

  selectAllAnnotationsBySpine = ({ spineId, selected }: { spineId: string, selected: boolean }): void => {
    this.store.dispatch(annotationsActions.selectAnnotationBySpineId({ spineId, selected }));
  };

  selectAnnotationByResource(
    { spineId, resourceUuid, selected }: { spineId: string, resourceUuid: string, selected: boolean },
  ): void {
    this.store.dispatch(annotationsActions.selectAnnotationByResourceUUID({ spineId, resourceUuid, selected }));
  }

  clearSelectedAnnotations(): void {
    this.store.dispatch(annotationsActions.deselectAllAnnotationsInList());
  }

  bulkDelete(annotationsType: MaterialAnnotationType): void {
    console.log('Bulk delete annotations of type:', annotationsType);
    this.store.dispatch(annotationsActions.bulkDeleteSelectedAnnotations({ annotationsType }));
  }

  exportAnnotations(type: MaterialAnnotationType): void {
    this.store.dispatch(
      annotationsActions.fetchAnnotationsForAllSpinesByType({ annotationType: type }),
    );
  }

  completeExport(type: MaterialAnnotationType): void {
    const annotationType: Annotations = type === MaterialAnnotationType.HIGHLIGHTS
      ? 'highlight'
      : 'note';
    this.store.dispatch(annotationsActions.exportAnnotations({ annotationType }));
  }
}
